import React, { useEffect } from "react"
import { ReactSVG } from "react-svg"

import { graphql } from "gatsby"
import { ContentBlockFieldsFragment } from "../../../../graphql-types"

import * as styles from "./content-block.module.scss"
import { elementOffset, transformItem } from "../../../components/common/functions/functions"
import RichDatoCmsContent from "../../../components/common/jsx/rich-dato-cms-content/rich-dato-cms-content"
import useWindowHeight from "../../../hooks/use-window-height"
import useWindowWidth from "../../../hooks/use-window-width"
import arrow from "../../../../static/vectors/arrow.svg"
import mobileArrow from "../../../../static/vectors/buttonArrowMobile.svg"
import { useLocale } from "../../../context/locale-context"

type ContentBlockProps = { data: ContentBlockFieldsFragment }

const ContentBlock: React.FC<ContentBlockProps> = ({ data }) => {
  const { locale, defaultLocale } = useLocale()
  const isBeforeMobile = useWindowWidth() < 1200
  const isMobile = useWindowWidth() <= 825
  const circlePosition = isBeforeMobile ? "-7.5vw" : isMobile ? "-60px" : "-120px"
  const circleOrange = { backgroundColor: "#D14000", bottom: circlePosition, right: circlePosition }
  const circleBlue = { backgroundColor: "#33CCDD", bottom: circlePosition, left: circlePosition }
  const circleOptions = [circleOrange, circleBlue]
  const circleStyle = circleOptions[Math.floor(Math.random() * circleOptions.length)]
  const windowHeight = useWindowHeight()

  const displayImage = (image): JSX.Element => {
    if (image.format === "svg") {
      return <ReactSVG src={image.url} className={data.imageHasCircleShape ? styles.circleImage : styles.squareImage} />
    } else {
      return (
        <img
          src={image.url}
          alt={image.alt || ""}
          className={data.imageHasCircleShape ? styles.circleImage : styles.squareImage}
        />
      )
    }
  }

  const handleAnimation = (): void => {
    const structuredTextContainer = document.getElementById("structuredTextContainer")
    const structuredTextElements = document.querySelectorAll("h1, h2, h3, h4, h5, h6, p, a")
    const imageElements = document.querySelectorAll("aside")

    const renderContentBlock = () => {
      Array.from(structuredTextElements).forEach(text => {
        if (window.scrollY + windowHeight >= elementOffset(text).top - structuredTextContainer.clientHeight - 300) {
          transformItem(text, 400)
        }
      })
      Array.from(imageElements).forEach(img => {
        if (window.scrollY + windowHeight >= elementOffset(img).top - structuredTextContainer.clientHeight - 300) {
          transformItem(img, 400)
        }
      })
    }

    if (structuredTextContainer) {
      renderContentBlock()
      document.addEventListener("scroll", () => renderContentBlock())
    }
  }

  useEffect(() => {
    handleAnimation()
  }, [])

  return (
    <section className={styles.contentBlock} style={{ backgroundColor: data.grayBackground ? "#EEEEEE" : "white" }}>
      <div className={styles.content}>
        <div className={styles.mainContent}>
          {(data.imageOnLeftSide || isMobile) && (
            <aside className={data.imageHasCircleShape ? styles.circleImageContainer : styles.squareImageContainer}>
              {displayImage(data.image)}
              {data.imageHasCircleShape ? (
                <div className={styles.roundImageCircle} />
              ) : (
                <div className={styles.circle} style={circleStyle} />
              )}
            </aside>
          )}
          <div id={"structuredTextContainer"} className={styles.structuredTextContainer}>
            <RichDatoCmsContent data={data.structuredText} />
            {data.button && (
              <a
                className={styles.button}
                href={
                  data.isInternalLink
                    ? locale === defaultLocale
                      ? `/${data.buttonLink}`
                      : `/${locale}/${data.buttonLink}`
                    : data.buttonLink
                }
              >
                {data.buttonTitle}
                <div className={styles.arrowContainer}>
                  <ReactSVG src={isMobile ? mobileArrow : arrow} className={styles.arrow} />
                  <ReactSVG src={isMobile ? mobileArrow : arrow} className={styles.arrow} />
                </div>
              </a>
            )}
          </div>
          {!data.imageOnLeftSide && !isMobile && (
            <aside className={data.imageHasCircleShape ? styles.circleImageContainer : styles.squareImageContainer}>
              {displayImage(data.image)}
              {data.imageHasCircleShape ? (
                <div className={styles.roundImageCircle} />
              ) : (
                <div className={styles.circle} style={circleStyle} />
              )}
            </aside>
          )}
        </div>
      </div>
    </section>
  )
}

export const fragment = graphql`
  fragment ContentBlockFields on DatoCmsOurApproachContentBlock {
    __typename
    id
    button
    buttonLink
    buttonTitle
    imageHasCircleShape
    imageOnLeftSide
    isInternalLink
    grayBackground
    image {
      alt
      format
      url
    }
    structuredText {
      ...StructuredTextFields
    }
  }
`

export default ContentBlock
